import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {
  ArchiveIcon,
  EyeClosedIcon,
  GrabberIcon,
  KebabHorizontalIcon,
  NumberIcon,
  PencilIcon,
  TrashIcon,
  ZapIcon,
} from '@primer/octicons-react'
import {ActionList, type ActionListItemProps, ActionMenu, IconButton} from '@primer/react'
import {useCallback, useMemo, useRef} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {BoardColumnActionMenuUI} from '../../api/stats/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import type {MemexItemModel} from '../../models/memex-item-model'
import {Link} from '../../router'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_SETTINGS_FIELD_ROUTE} from '../../routes'
import {Resources, WorkflowResources} from '../../strings'
import {MoveToLeftAction, MoveToRightAction} from '../move-to-actions'
import styles from './group-menu.module.css'

interface GroupMenuProps {
  name: string
  isColumn?: boolean
  open: boolean
  setOpen: (open: boolean) => void
  /** Callback to be called when the group is renamed */
  onRename?: () => void
  onEditDetails?: () => void
  onEditLimit?: () => void
  onAddAutoAssignWorkflow?: () => void
  onHide?: () => void
  onDelete?: () => void
  onMoveToLeft?: () => void
  onMoveToRight?: () => void
  /** Callback when user clicks "Move items to…" */
  onMoveItems?: () => void
  showEditField?: boolean
  showEditMilestones?: boolean
  moveToLeftDisabled?: boolean
  moveToRightDisabled?: boolean
  isSingleSelect?: boolean
  /** The list of items belonging to this column */
  items: ReadonlyArray<MemexItemModel>
  /** The column id for field editing */
  fieldId?: string | number
  /** The milestone URL for milestone editing */
  milestoneURL?: string | null
}

export const GroupMenu = ({
  name,
  isColumn = false,
  open,
  setOpen,
  onRename,
  onEditLimit,
  onEditDetails,
  onAddAutoAssignWorkflow,
  onHide,
  onDelete,
  onMoveToLeft,
  onMoveToRight,
  onMoveItems,
  showEditMilestones,
  showEditField,
  moveToLeftDisabled,
  moveToRightDisabled,
  isSingleSelect,
  items,
  fieldId,
  milestoneURL,
}: GroupMenuProps) => {
  const {hasWritePermissions} = ViewerPrivileges()
  const nonRedactedItems = useMemo(() => items.filter(item => item.contentType !== ItemType.RedactedItem), [items])
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation()
  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation()

  const onArchiveAll = useCallback(() => {
    if (items.length === 0) return
    const itemIds = items.map(item => item.id)
    openArchiveConfirmationDialog(
      itemIds,
      BoardColumnActionMenuUI,
      Resources.archiveGroupItemsConfirmationTitle,
      Resources.archiveGroupItemsConfirmationMessage(items.length),
    )
  }, [openArchiveConfirmationDialog, items])

  const onDeleteAll = useCallback(() => {
    if (items.length === 0) return
    const itemIds = items.map(item => item.id)
    openRemoveConfirmationDialog(itemIds, BoardColumnActionMenuUI, {
      title: Resources.deleteGroupItemsConfirmationTitle,
      content: Resources.deleteGroupItemsConfirmationMessage(items.length),
    })
  }, [openRemoveConfirmationDialog, items])

  const groupActions = []

  if (onRename && hasWritePermissions) {
    groupActions.push(<RenameGroupAction onSelect={onRename} key="rename" />)
  }
  if (onEditDetails && hasWritePermissions) {
    groupActions.push(<EditGroupDetailsAction onSelect={onEditDetails} key="editDetails" />)
  }
  if (onEditLimit && hasWritePermissions) {
    groupActions.push(<EditGroupLimitAction onSelect={onEditLimit} key="editLimit" />)
  }
  if (onAddAutoAssignWorkflow && hasWritePermissions) {
    groupActions.push(<AddAutoAssignWorkflowAction onSelect={onAddAutoAssignWorkflow} key="addAutoAssignWorkflow" />)
  }
  if (showEditField && hasWritePermissions) {
    groupActions.push(<EditFieldAction fieldId={fieldId} key="editField" />)
  }
  if (showEditMilestones && hasWritePermissions) {
    groupActions.push(<EditMilestonesAction milestoneURL={milestoneURL} key="editMilestones" />)
  }
  if (onHide) {
    groupActions.push(<HideGroupAction onSelect={onHide} key="hide" />)
  }
  if (onDelete && hasWritePermissions) {
    groupActions.push(<DeleteGroupAction onSelect={onDelete} key="delete" />)
  }

  const itemActions = []

  if (onMoveItems && hasWritePermissions) {
    itemActions.push(
      <MoveItemsToIterationAction disabled={nonRedactedItems.length === 0} onSelect={onMoveItems} key="moveItems" />,
    )
  }

  if (hasWritePermissions) {
    itemActions.push(
      <ArchiveItemsAction disabled={nonRedactedItems.length === 0} onSelect={onArchiveAll} key="archiveItems" />,
    )
    itemActions.push(
      <DeleteItemsAction disabled={nonRedactedItems.length === 0} onSelect={onDeleteAll} key="deleteItems" />,
    )
  }

  const positionActions = []

  if (onMoveToLeft && hasWritePermissions) {
    const isDisabled = moveToLeftDisabled || !isSingleSelect

    positionActions.push(
      <MoveToLeftAction
        disabled={isDisabled}
        isLeftmostColumn={isSingleSelect && moveToLeftDisabled}
        columnMoveNotAvailable={isSingleSelect === false ? 'iterations' : undefined}
        onSelect={onMoveToLeft}
        key="moveToLeft"
      />,
    )
  }
  if (onMoveToRight && hasWritePermissions) {
    const isDisabled = moveToRightDisabled || !isSingleSelect

    positionActions.push(
      <MoveToRightAction
        disabled={isDisabled}
        isRightmostColumn={isSingleSelect && moveToRightDisabled}
        columnMoveNotAvailable={isSingleSelect === false ? 'iterations' : undefined}
        onSelect={onMoveToRight}
        key="moveToRight"
      />,
    )
  }

  if (itemActions.length === 0 && groupActions.length === 0 && positionActions.length === 0) return null

  return (
    <ActionMenu open={open} onOpenChange={setOpen} anchorRef={menuButtonRef}>
      <ActionMenu.Anchor>
        <IconButton
          ref={menuButtonRef}
          aria-label={Resources.groupHeaderMenu(name, isColumn)}
          aria-haspopup="menu"
          variant="invisible"
          icon={KebabHorizontalIcon}
          size="small"
          className={styles.IconButton}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end">
        <ActionList role="menu">
          {groupActions.length > 0 && (
            <ActionList.Group>
              <ActionList.GroupHeading>{isColumn ? 'Column' : 'Group'}</ActionList.GroupHeading>
              {groupActions}
            </ActionList.Group>
          )}
          {itemActions.length > 0 && (
            <>
              {groupActions.length > 0 && <ActionList.Divider />}
              <ActionList.Group>
                <ActionList.GroupHeading>Items</ActionList.GroupHeading>
                {itemActions}
              </ActionList.Group>
            </>
          )}
          {positionActions.length > 0 && (
            <>
              <ActionList.Divider />
              <ActionList.Group>
                <ActionList.GroupHeading>Position</ActionList.GroupHeading>
                {positionActions}
              </ActionList.Group>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const RenameGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <PencilIcon />
    </ActionList.LeadingVisual>
    Rename
  </ActionList.Item>
)

const EditGroupLimitAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <NumberIcon />
    </ActionList.LeadingVisual>
    Set limit
  </ActionList.Item>
)

const EditGroupDetailsAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <PencilIcon />
    </ActionList.LeadingVisual>
    Edit details
  </ActionList.Item>
)

const AddAutoAssignWorkflowAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <ZapIcon />
    </ActionList.LeadingVisual>
    {WorkflowResources.addAssignCopilotWorkflow}
    <ActionList.TrailingVisual>
      <BetaLabel />
    </ActionList.TrailingVisual>
  </ActionList.Item>
)

const EditFieldAction = ({fieldId}: {fieldId?: string | number}) => {
  const projectRouteParams = useProjectRouteParams()
  if (!(fieldId && projectRouteParams)) return null
  return (
    <ActionList.LinkItem
      as={Link}
      to={{
        pathname: PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
          ...projectRouteParams,
          fieldId,
        }),
      }}
    >
      <ActionList.LeadingVisual>
        <PencilIcon />
      </ActionList.LeadingVisual>
      Edit field
    </ActionList.LinkItem>
  )
}

/**
 * Action component for editing milestone details
 * @param milestoneURL - The full URL path to the milestone ("/monalisa/smile/milestone/1")
 */
const EditMilestonesAction = ({milestoneURL}: {milestoneURL?: string | null}) => {
  if (!milestoneURL) return null
  return (
    <ActionList.LinkItem as="a" href={milestoneURL}>
      <ActionList.LeadingVisual>
        <PencilIcon />
      </ActionList.LeadingVisual>
      Edit details
    </ActionList.LinkItem>
  )
}

const HideGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <EyeClosedIcon />
    </ActionList.LeadingVisual>
    Hide from view
  </ActionList.Item>
)

const DeleteGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item variant="danger" {...props}>
    <ActionList.LeadingVisual>
      <TrashIcon />
    </ActionList.LeadingVisual>
    Delete
  </ActionList.Item>
)

const ArchiveItemsAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <ArchiveIcon />
    </ActionList.LeadingVisual>
    Archive all
  </ActionList.Item>
)

const DeleteItemsAction = (props: ActionListItemProps) => (
  <ActionList.Item variant="danger" {...props}>
    <ActionList.LeadingVisual>
      <TrashIcon />
    </ActionList.LeadingVisual>
    Remove all
  </ActionList.Item>
)

const MoveItemsToIterationAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props}>
    <ActionList.LeadingVisual>
      <GrabberIcon />
    </ActionList.LeadingVisual>
    {Resources.moveItemsToIterationLabel}
  </ActionList.Item>
)
